import { CompilerProfileSummary } from '@/components/compiler/CompilerProfileSummary';
import { fdaSupplementEffectiveDateOptions, fdaSupplementEffectiveDateError, FDA_SUPPLEMENT_EFFECTIVE_DATES } from '@shared/fdaSubmissionVocabulary';
import { fdaActivityStartSequenceError } from '@shared/fdaActivityStartSequence';
import { describeApiError } from '@/lib/apiErrorView';
import { initialSequenceNumberForRegion } from '@shared/initialSequence';
import {
  applicationIdentifierSpec,
  type SequenceApplicationIdentifierField,
} from '@shared/applicationIdentity';
import { useSubmissionWorkspace } from '@/hooks/useSubmissionWorkspace';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  v2,
  ApiError,
  parseApiErrorBody,
  type CompanyProfile,
  type Region,
  type SpecFileResolution,
  type Submission,
  type SubmissionTypeGroup,
  type ParallelRegionSetup,
  type ParallelSharedMetadata,
} from '@/api/v2Client';
import { REGIONS, REGION_FLAGS } from '@/constants/regions';
import {
  parseSeq,
  normalizeSeq,
  nextSequenceNumber,
  isSequenceTaken,
  liveSequences,
  duplicateSequenceMessage,
  existingApplicationMessage,
} from '@/lib/sequenceUniqueness';
import {
  FDA_PATHWAYS,
  EMA_PROCEDURES,
} from '@/constants/ctd';
import { getHcSequenceDescriptionsForType, HC_SEQUENCE_DESCRIPTIONS } from '@/constants/hc-sequence-descriptions';
import { ParallelRegionSetups } from '@/components/submissions/ParallelRegionSetups';
import {
  emptyRegionDraft,
  parallelActivityStartError,
  identifierSpecForRegion,
  type ParallelRegionDraft,
} from '@/components/submissions/parallelRegionSetup';

// Phase 5.26 — New Submission Wizard.
//
// 4-step wizard covering V1's NewSubmissionScreen.jsx fields. The
// backend already accepts every field V1 collects (per
// `src/routes/submissions.ts:ALLOWED_WRITE_FIELDS`), so this phase is
// purely frontend: take the existing single-page form and chunk it
// into Application / Submission / Sequence / Review steps with
// progress indicator, per-step validation, and back/next navigation.
//
// Field provenance vs V1:
//   Step 1 (Application): DrugName, CompanyProfileId, InnName,
//                         TherapeuticArea, Indication
//   Step 2 (Submission):  Region, SubmissionType, SequenceNumber,
//                         {Nda|EuApp|Dossier}, ApplicationPathway,
//                         ControlNumber, EctdVersion, resolved spec (T15-D8)
//   Step 3 (Sequence):    SequencePurpose, SequenceDescription,
//                         DevelopmentPhase
//   Step 4 (Review):      read-only summary + Create button

// ----- Region / option helpers -----

/** Default to the implemented compiler format; other versions remain explicit choices. */
function defaultEctdVersion(): string {
  return '3.2.2';
}

// T15-D8 — the wizard no longer maps regions to spec-file codes itself (the
// old 'us'/'eu'/'ca' map matched nothing after 20260524). It asks the server
// which specification creation will pin (GET /api/v1/spec-files/resolve),
// passing the stored region value; the server canonicalises it.
type SpecPreview =
  | { readonly state: 'loading' }
  | { readonly state: 'error'; readonly message: string }
  | { readonly state: 'resolved'; readonly resolution: SpecFileResolution };

function specPreviewText(p: SpecPreview): string {
  if (p.state === 'loading') return 'Resolving specification…';
  if (p.state === 'error') return p.message;
  const { spec, coverage, regionCode } = p.resolution;
  if (!spec) return 'None configured';
  const shared =
    coverage === 'shared' ? ` — shared ${spec.Region} file, also covers ${regionCode ?? ''}` : '';
  return `${spec.Title}${shared}`;
}

/** A supplement inherits its application's pin (resolveFamilyPin), not the region default. */
function specPreviewLine(form: { readonly filingType: string }, p: SpecPreview): string {
  if (form.filingType === 'supplement') {
    return 'Will file under: the specification the existing application is pinned to';
  }
  return `Will file under: ${specPreviewText(p)}`;
}

/** V1-style sequence-description seed based on submission type. */
function defaultSequenceDescription(submissionType: string): string {
  const t = submissionType.toLowerCase();
  if (!t) return '';
  if (t === 'nda' || t === 'bla' || t === 'ind' || t === 'nds' || t === 'maa') {
    return 'Initial submission';
  }
  if (t.includes('variation') || t.includes('supplement') || t === 'snds' || t === 'snds-c') {
    return 'Supplement -- ';
  }
  if (t.includes('amendment')) return 'Amendment -- ';
  if (t.includes('annual')) return 'Annual Report -- Year ';
  if (t.includes('psur') || t.includes('renewal')) return `${submissionType} -- `;
  return '';
}

// Phase phase-fix-new-submission-wizard-supplement — original vs supplement.
// '' = not yet answered (wizard behaves as before — no seq lock, all types).
// 'original' = brand-new application (sequence LOCKED to the region's initial
// number, `initialSequenceNumberForRegion`: 0001 for FDA and TGA, 0000 elsewhere).
// 'supplement' = continues an existing application (sequence auto-detected).
type FilingType = '' | 'original' | 'supplement';

/**
 * The wizard form field that backs each canonical application-identifier field.
 * The region → identifier-field decision itself lives in the one canonical
 * evaluator (`applicationIdentifierSpec`); this only maps that field to the
 * matching `WizardFormState` key.
 */
const FORM_FIELD_BY_IDENTIFIER: Readonly<
  Record<SequenceApplicationIdentifierField, 'ndaNumber' | 'euApplicationUuid' | 'dossierId'>
> = {
  NdaNumber: 'ndaNumber',
  EuApplicationUuid: 'euApplicationUuid',
  DossierId: 'dossierId',
};

/**
 * The application-identifier field for the current region — the value that,
 * together with the region, uniquely names an application. `formField` is the
 * wizard form key; `subField` is the matching `Submission` column used to find
 * existing sequences. Resolved through the canonical evaluator so TGA →
 * DossierId and PMDA / Swissmedic → NdaNumber stay in step with the backend.
 * Returns null for any region the canonical evaluator does not recognise — we
 * never guess an identifier for an unknown region.
 */
function applicationIdentifier(
  form: WizardFormState,
): { formField: 'ndaNumber' | 'euApplicationUuid' | 'dossierId'; subField: keyof Submission; value: string } | null {
  const spec = applicationIdentifierSpec(form.region);
  if (!spec) return null;
  const formField = FORM_FIELD_BY_IDENTIFIER[spec.field];
  return { formField, subField: spec.field, value: form[formField].trim() };
}

// feat/wizard-duplicate-sequence-guard — `parseSeq` / `nextSequenceNumber` moved
// to `@/lib/sequenceUniqueness` (ONE rule for "1" ≡ "0001", shared with the
// duplicate validator and its unit tests). Imported above.

/**
 * feat/wizard-duplicate-sequence-guard — result of the "is this sequence
 * already taken" check on the Submission step. Reuses the lookupStatus
 * vocabulary ('idle' | 'loading' | 'found' | 'none') plus an explicit 'error'
 * for a failed lookup (which must NOT block — the server check still applies).
 */
type DuplicateCheck =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'none' }
  | {
      readonly status: 'found';
      /** True on the original path (initial sequence locked): the APPLICATION already exists. */
      readonly original: boolean;
      readonly message: string;
      readonly nextSequence: string;
      /** Highest existing sequence's id — the supplement entry point. */
      readonly supplementOfId: string | null;
      /** Set when the 409 came from the server (final safety net). */
      readonly fromServer?: boolean;
    }
  | { readonly status: 'error'; readonly message: string };

/** The key the duplicate check is keyed on — change it and the check re-runs. */
function duplicateCheckKey(form: WizardFormState): string {
  const id = applicationIdentifier(form);
  return `${form.region}|${id?.value ?? ''}|${form.filingType}|${normalizeSeq(form.sequenceNumber) ?? form.sequenceNumber}`;
}

/** Pure evaluation of the lookup result against the form (no I/O). */
function evaluateDuplicate(form: WizardFormState, matches: readonly Submission[]): DuplicateCheck {
  const id = applicationIdentifier(form);
  if (!id || id.value === '') return { status: 'idle' };
  // feat/wizard-duplicate-sequence-guard — only a TECHNICALLY rejected sequence
  // gives up its slot (the server's shared predicate, @shared/sequenceSlot).
  // Evaluate collision AND next-free against the occupying family only, so the
  // wizard never disagrees with the server about a reuse, nor over-counts the
  // next sequence.
  const live = liveSequences(matches);
  if (form.filingType === 'original') {
    // Sequence locked to the regional initial number: ANY occupying sequence means the application
    // exists, so this cannot be a new original — point at the supplement path.
    if (live.length === 0) return { status: 'none' };
    const parent = [...live].sort(
      (a, b) => parseSeq(b.SequenceNumber) - parseSeq(a.SequenceNumber),
    )[0]!;
    return {
      status: 'found',
      original: true,
      message: existingApplicationMessage({ region: form.region, identifier: id.value, existing: live }),
      nextSequence: nextSequenceNumber(live),
      supplementOfId: parent.Id,
    };
  }
  if (!isSequenceTaken(live, form.sequenceNumber)) return { status: 'none' };
  return {
    status: 'found',
    original: false,
    message: duplicateSequenceMessage({
      region: form.region,
      identifier: id.value,
      sequenceNumber: form.sequenceNumber,
      existing: live,
    }),
    nextSequence: nextSequenceNumber(live),
    supplementOfId: null,
  };
}

const SEQUENCE_PURPOSE_OPTIONS = [
  'Initial Submission',
  'Labeling Update',
  'CMC Change',
  'Safety Update',
  'Clinical Data',
  'Administrative',
  'Manufacturing Site Change',
  'Other',
] as const;

const DEVELOPMENT_PHASE_OPTIONS = [
  'Phase 1',
  'Phase 2',
  'Phase 3',
  'Post-Approval',
  'Pre-submission',
] as const;

// ----- Styles -----

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  fontSize: 13,
  boxSizing: 'border-box',
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: 4,
};

const requiredMark: CSSProperties = { color: 'var(--red)', marginLeft: 2 };

// phase-ui-review-F10 — the global AI launcher (AppShell) is fixed at the
// viewport's bottom-right: right 20 + bottom 20, 48px square. At narrow widths
// it floated over the wizard's Next button. Reserve a clear zone to the right
// of (and below) the footer action cluster so the FAB never overlaps it:
// 20 (right gutter) + 48 (FAB) + 12 (breathing room) = 80px.
const FAB_SAFE_AREA_PX = 80;

const errorStyle: CSSProperties = {
  color: 'var(--red)',
  fontSize: 12,
  marginTop: 4,
};

const primaryButton: CSSProperties = {
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButton: CSSProperties = {
  background: 'var(--surface)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '10px 20px',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

// ----- Wizard state -----

const STEP_LABELS = ['Application', 'Submission', 'Sequence', 'Review'] as const;
type WizardStep = 0 | 1 | 2 | 3;

interface WizardFormState {
  drugName: string;
  companyProfileId: string;
  innName: string;
  therapeuticArea: string;
  indication: string;
  region: Region;
  /** Legacy free-text field — kept for HC and regions not yet in catalog. */
  submissionType: string;
  /** Phase KB-submission-types-v3 — catalog application type (e.g. NDA, MAA). */
  applicationType: string;
  /** Phase KB-submission-types-v3 — catalog submission type code (e.g. fdast1). */
  submissionTypeCode: string;
  /** Phase KB-submission-types-v3 — catalog sub-type code (e.g. fdasst1). */
  submissionSubTypeCode: string;
  sequenceNumber: string;
  fdaActivityStartSequence: string;
  supplementEffectiveDateType: string;
  /**
   * Phase phase-fix-new-submission-wizard-supplement — original vs supplement
   * gate (Step 2). Drives sequence locking (original → 0000) vs auto-detection
   * (supplement → max+1) and the submission-type filter.
   */
  filingType: FilingType;
  /**
   * Phase phase-fix-new-submission-wizard-supplement — set to the existing
   * application's highest-sequence submission on the supplement path so the
   * backend can link the chain (lifecycle / prior-sequence logic).
   */
  parentSubmissionId: string;
  ndaNumber: string;
  applicationPathway: string;
  controlNumber: string;
  euApplicationUuid: string;
  dossierId: string;
  ectdVersion: string;
  sequencePurpose: string;
  sequenceDescription: string;
  developmentPhase: string;
}

function initialForm(): WizardFormState {
  return {
    drugName: '',
    companyProfileId: '',
    innName: '',
    therapeuticArea: '',
    indication: '',
    region: 'FDA',
    submissionType: '',
    applicationType: '',
    submissionTypeCode: '',
    submissionSubTypeCode: '',
    // The default region is FDA, so the default initial sequence is FDA's 0001
    // (FDA M1 v2.6 III.B.2.b "should start at 0001"); region changes re-derive it.
    sequenceNumber: initialSequenceNumberForRegion('FDA'),
    fdaActivityStartSequence: '',
    supplementEffectiveDateType: '',
    filingType: '',
    parentSubmissionId: '',
    ndaNumber: '',
    applicationPathway: '',
    controlNumber: '',
    euApplicationUuid: '',
    dossierId: '',
    ectdVersion: '3.2.2',
    sequencePurpose: '',
    sequenceDescription: '',
    developmentPhase: '',
  };
}

interface ValidationResult {
  ok: boolean;
  errors: Partial<Record<keyof WizardFormState, string>>;
}

function validateStep(step: WizardStep, form: WizardFormState): ValidationResult {
  const errors: Partial<Record<keyof WizardFormState, string>> = {};
  if (step === 0) {
    if (!form.drugName.trim()) errors.drugName = 'Drug name is required';
  } else if (step === 1) {
    // F19: this required choice determines sequence identity, not just copy.
    if (!form.filingType) errors.filingType = 'Choose a new original application or a supplement';
    // Either the catalog applicationType OR legacy submissionType must be set.
    if (!form.applicationType && !form.submissionType)
      errors.submissionType = 'Application type is required';
    if (!form.sequenceNumber.trim()) errors.sequenceNumber = 'Sequence number is required';
    if (form.region === 'FDA') {
      const activityStartError = fdaActivityStartSequenceError(form.fdaActivityStartSequence, normalizeSeq(form.sequenceNumber));
      if (activityStartError) errors.fdaActivityStartSequence = activityStartError;
      const effectiveError = fdaSupplementEffectiveDateError(form.submissionTypeCode, form.submissionSubTypeCode, form.supplementEffectiveDateType);
      if (effectiveError) errors.supplementEffectiveDateType = effectiveError;
    }
  } else if (step === 2) {
    if (!form.sequenceDescription.trim()) {
      errors.sequenceDescription = 'Sequence description is required';
    }
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

// ----- Step components -----

interface StepProps {
  readonly form: WizardFormState;
  readonly errors: ValidationResult['errors'];
  readonly update: <K extends keyof WizardFormState>(key: K, value: WizardFormState[K]) => void;
}

interface Step1ApplicationProps extends StepProps {
  readonly companies: CompanyProfile[] | null;
  readonly companyError: string | null;
  /** phase-fix-new-supplement-routing — supplement flow: application-identity
   * fields are inherited from the parent and read-only. */
  readonly lockedIdentity?: boolean;
}

function Step1Application({
  form,
  errors,
  lockedIdentity = false,
  update,
  companies,
  companyError,
}: Step1ApplicationProps): ReactElement {
  return (
    <fieldset
      aria-labelledby="step-1-heading"
      style={{ border: 'none', padding: 0, margin: 0 }}
    >
      <h3 id="step-1-heading" style={{ margin: '0 0 12px 0' }}>
        Application details
      </h3>
      {companyError && (
        <div role="alert" style={{ color: 'var(--amber)', marginBottom: 12 }}>
          {companyError}
        </div>
      )}
      {companies !== null && companies.length === 0 && !companyError && (
        <div
          style={{
            background: 'var(--amber-bg)',
            padding: 12,
            borderRadius: 6,
            marginBottom: 12,
            fontSize: 13,
          }}
        >
          No company profiles yet. You can create the submission anyway, but it won&apos;t be linked
          to a company.
        </div>
      )}
      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr 1fr' }}>
        <label style={{ gridColumn: '1 / -1' }}>
          <span style={labelStyle}>
            Drug name<span style={requiredMark}>*</span>
          </span>
          <input
            value={form.drugName}
            onChange={(e) => update('drugName', e.target.value)}
            disabled={lockedIdentity}
            data-testid="wizard-drugname"
            aria-invalid={errors.drugName ? true : undefined}
            style={inputStyle}
          />
          {errors.drugName && <div style={errorStyle}>{errors.drugName}</div>}
        </label>

        <label>
          <span style={labelStyle}>Company / Applicant</span>
          <select
            value={form.companyProfileId}
            onChange={(e) => update('companyProfileId', e.target.value)}
            disabled={companies === null || lockedIdentity}
            style={inputStyle}
          >
            {/* Phase phase-ui-consistency-sweep (§3D) — consistent loading
                pattern: "Loading…" while in flight, then the optional
                "— None —" choice (company is optional). */}
            <option value="">{companies === null ? 'Loading…' : '— None —'}</option>
            {(companies ?? []).map((c) => (
              <option key={c.Id} value={c.Id}>
                {c.CompanyName}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span style={labelStyle}>INN (International Non-proprietary Name)</span>
          <input
            value={form.innName}
            onChange={(e) => update('innName', e.target.value)}
            style={inputStyle}
          />
        </label>

        <label>
          <span style={labelStyle}>Therapeutic area</span>
          <input
            value={form.therapeuticArea}
            onChange={(e) => update('therapeuticArea', e.target.value)}
            placeholder="e.g. Oncology"
            style={inputStyle}
          />
        </label>

        <label style={{ gridColumn: '1 / -1' }}>
          <span style={labelStyle}>Indication</span>
          <input
            value={form.indication}
            onChange={(e) => update('indication', e.target.value)}
            style={inputStyle}
          />
        </label>
      </div>
    </fieldset>
  );
}

interface Step2SubmissionProps extends StepProps {
  /** T15-D8 — the server's answer to "which spec will creation pin?". */
  readonly specPreview: SpecPreview;
  /** Phase KB-submission-types-v3 — catalog data keyed by applicationType. */
  readonly catalogByAppType: Map<string, SubmissionTypeGroup[]>;
  readonly catalogAppTypes: string[];
  readonly catalogLoading: boolean;
  /** Batched application-type change — single setState so goNext sees the update. */
  readonly onAppTypeChange: (val: string) => void;
  /** Phase phase-fix-new-submission-wizard-supplement — original/supplement gate. */
  readonly onFilingTypeChange: (val: FilingType) => void;
  /** phase-fix-new-supplement-routing — lock region + identifier + filing
   * type when entered as a supplement of a specific application. */
  readonly lockedIdentity?: boolean;
  /** Fired on blur of the region's application-identifier field (NDA / EU UUID / Dossier). */
  readonly onIdentifierBlur: () => void;
  readonly lookupStatus: 'idle' | 'loading' | 'found' | 'none';
  readonly lookupMessage: string | null;
  /** feat/wizard-duplicate-sequence-guard — blocking duplicate state, if any. */
  readonly sequenceDuplicate: Extract<DuplicateCheck, { status: 'found' }> | null;
  /** Non-blocking notice: "Looking up…" or "Couldn't verify uniqueness". */
  readonly uniquenessNotice: string | null;
  /** "Use NNNN" — sets the sequence to the next free one. */
  readonly onUseNextSequence: () => void;
}

function Step2Submission({
  form,
  errors,
  update,
  specPreview,
  catalogByAppType,
  catalogAppTypes,
  catalogLoading,
  onAppTypeChange,
  onFilingTypeChange,
  lockedIdentity = false,
  onIdentifierBlur,
  lookupStatus,
  lookupMessage,
  sequenceDuplicate,
  uniquenessNotice,
  onUseNextSequence,
}: Step2SubmissionProps): ReactElement {
  // Derive the submission types for the currently selected applicationType,
  // filtered by the original/supplement gate (Phase
  // phase-fix-new-submission-wizard-supplement §4). The catalog's
  // `requiresPriorDocumentSet` is V2's analog of V1's `isOriginal`: a type that
  // builds on a prior document set IS a supplement/amendment. When the gate is
  // unanswered ('') no filter is applied (legacy behaviour).
  const currentSubmissionTypes = (
    form.applicationType ? (catalogByAppType.get(form.applicationType) ?? []) : []
  ).filter((t) => {
    if (form.filingType === 'original') return t.requiresPriorDocumentSet !== true;
    if (form.filingType === 'supplement') return t.requiresPriorDocumentSet === true;
    return true;
  });

  const isOriginal = form.filingType === 'original';
  const isSupplement = form.filingType === 'supplement';

  // Derive the sub-types for the currently selected submissionTypeCode.
  const currentSubTypes = form.submissionTypeCode
    ? (currentSubmissionTypes.find((t) => t.code === form.submissionTypeCode)?.subTypes ?? [])
    : [];

  return (
    <fieldset
      aria-labelledby="step-2-heading"
      style={{ border: 'none', padding: 0, margin: 0 }}
    >
      <h3 id="step-2-heading" style={{ margin: '0 0 12px 0' }}>
        Submission details
      </h3>

      {/* Phase phase-fix-new-submission-wizard-supplement (§3A) — the
          original-vs-supplement gate. V1's wording. The choice drives sequence
          handling: original → locked to the regional initial sequence; supplement → look up the existing
          application and auto-detect the next sequence. */}
      <div
        data-testid="filing-type-gate"
        role="radiogroup"
        aria-label="Is this a new application or a supplement?"
        style={{
          marginBottom: 20,
          padding: '14px 16px',
          border: '1px solid var(--border)',
          borderRadius: 8,
          background: 'var(--surface2)',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
          Has an eCTD submission already been filed with the agency for this product?
          <span style={requiredMark}>*</span>
        </div>
        <p style={{ margin: '0 0 10px 0', fontSize: 12, color: 'var(--text-muted)' }}>
          This determines whether we start a new application at sequence {initialSequenceNumberForRegion(form.region)} or continue an
          existing one.
        </p>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer', marginBottom: 6 }}>
          <input
            type="radio"
            name="filingType"
            data-testid="filing-type-original"
            value="original"
            checked={isOriginal}
            disabled={lockedIdentity}
            onChange={() => onFilingTypeChange('original')}
          />
          <span>
            <strong>No — new original application.</strong> Sequence number is locked to{' '}
            <code>{initialSequenceNumberForRegion(form.region)}</code>.
          </span>
        </label>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, cursor: 'pointer' }}>
          <input
            type="radio"
            name="filingType"
            data-testid="filing-type-supplement"
            value="supplement"
            checked={isSupplement}
            disabled={lockedIdentity}
            onChange={() => onFilingTypeChange('supplement')}
          />
          <span>
            <strong>Yes — supplement / amendment to an existing application.</strong> The next
            sequence number is auto-detected from the existing application.
          </span>
        </label>
        {errors.filingType && (
          <div style={errorStyle} data-testid="filing-type-error">
            {errors.filingType}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr 1fr' }}>
        <label>
          <span style={labelStyle}>
            Region<span style={requiredMark}>*</span>
          </span>
          <select
            value={form.region}
            onChange={(e) => update('region', e.target.value as Region)}
            disabled={lockedIdentity}
            data-testid="wizard-region"
            style={inputStyle}
          >
            {REGIONS.map((r) => (
              <option key={r} value={r}>
                {REGION_FLAGS[r]} {r}
              </option>
            ))}
          </select>
        </label>

        {/* Phase KB-submission-types-v3 — Application Type (cascading level 1) */}
        <label>
          <span style={labelStyle}>
            Application type<span style={requiredMark}>*</span>
          </span>
          <select
            value={form.applicationType}
            onChange={(e) => onAppTypeChange(e.target.value)}
            aria-invalid={errors.submissionType ? true : undefined}
            disabled={catalogLoading || lockedIdentity}
            style={inputStyle}
          >
            <option value="">
              {catalogLoading ? 'Loading…' : '— Select —'}
            </option>
            {catalogAppTypes.map((at) => (
              <option key={at} value={at}>
                {at}
              </option>
            ))}
          </select>
          {errors.submissionType && !form.applicationType && (
            <div style={errorStyle}>{errors.submissionType}</div>
          )}
        </label>

        {/* Phase KB-submission-types-v3 — Submission Type (cascading level 2) */}
        {form.applicationType && (
          <label>
            <span style={labelStyle}>
              Submission type<span style={requiredMark}>*</span>
            </span>
            <select
              value={form.submissionTypeCode}
              onChange={(e) => {
                update('submissionTypeCode', e.target.value);
                update('submissionSubTypeCode', '');
              }}
              aria-invalid={errors.submissionType ? true : undefined}
              style={inputStyle}
            >
              <option value="">— Select —</option>
              {currentSubmissionTypes.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.name}
                </option>
              ))}
            </select>
            {errors.submissionType && form.applicationType && !form.submissionTypeCode && (
              <div style={errorStyle}>{errors.submissionType}</div>
            )}
          </label>
        )}

        {/* Phase KB-submission-types-v3 — Sub-type (cascading level 3, only when sub-types exist) */}
        {currentSubTypes.length > 0 && (
          <label>
            <span style={labelStyle}>Sub-type</span>
            <select
              value={form.submissionSubTypeCode}
              onChange={(e) => update('submissionSubTypeCode', e.target.value)}
              style={inputStyle}
            >
              <option value="">— Select —</option>
              {currentSubTypes.map((st) => (
                <option key={st.code} value={st.code}>
                  {st.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          <span style={labelStyle}>
            Sequence number<span style={requiredMark}>*</span>
          </span>
          {/* Phase phase-fix-new-submission-wizard-supplement (§3A/§3B) —
              original: locked to the regional initial sequence (disabled, greyed). supplement: editable
              but strongly guided by the auto-detected value. unanswered: legacy
              free-text. */}
          <input
            value={form.sequenceNumber}
            onChange={(e) => update('sequenceNumber', e.target.value)}
            placeholder={initialSequenceNumberForRegion(form.region)}
            disabled={isOriginal}
            aria-invalid={errors.sequenceNumber || sequenceDuplicate ? true : undefined}
            aria-describedby={sequenceDuplicate ? 'sequence-duplicate-error' : undefined}
            data-testid="sequence-number-input"
            style={{
              ...inputStyle,
              fontFamily: 'monospace',
              ...(isOriginal ? { background: 'var(--surface2)', color: 'var(--text-muted)' } : {}),
            }}
          />
          {isOriginal && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Locked to {initialSequenceNumberForRegion(form.region)} for new applications.
            </div>
          )}
          {isSupplement && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              Auto-detected from the existing application. Edit only if you know the correct
              sequence.
            </div>
          )}
          {errors.sequenceNumber && <div style={errorStyle}>{errors.sequenceNumber}</div>}
          {/* feat/wizard-duplicate-sequence-guard — BLOCKING duplicate error.
              Supplement path: names the taken sequence and offers the next
              free one. Original path (initial sequence locked): the application already
              exists — offers the supplement entry point. */}
          {sequenceDuplicate && (
            <div
              id="sequence-duplicate-error"
              role="alert"
              data-testid="sequence-duplicate-error"
              style={errorStyle}
            >
              {sequenceDuplicate.message}{' '}
              {sequenceDuplicate.original ? (
                sequenceDuplicate.supplementOfId && (
                  <Link
                    to={`/submissions/new?supplementOf=${encodeURIComponent(sequenceDuplicate.supplementOfId)}`}
                    data-testid="sequence-duplicate-supplement-link"
                    style={{ color: 'var(--accent)', fontWeight: 600 }}
                  >
                    Continue as a supplement →
                  </Link>
                )
              ) : (
                // feat/wizard-duplicate-sequence-guard — only offer "Use NNNN"
                // when there is a real next sequence to apply. A server 409 can
                // omit it (empty string), and a "Use " button that blanks the
                // field is worse than no shortcut at all.
                normalizeSeq(sequenceDuplicate.nextSequence) !== null && (
                  <button
                    type="button"
                    data-testid="sequence-duplicate-use-next"
                    onClick={onUseNextSequence}
                    style={{
                      marginLeft: 4,
                      padding: '2px 8px',
                      fontSize: 12,
                      fontWeight: 600,
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      background: 'var(--surface)',
                      color: 'var(--accent)',
                      cursor: 'pointer',
                    }}
                  >
                    Use {sequenceDuplicate.nextSequence}
                  </button>
                )
              )}
            </div>
          )}
        </label>

        <label>
          <span style={labelStyle}>eCTD version</span>
          <input
            disabled={lockedIdentity}
            value={form.ectdVersion}
            onChange={(e) => update('ectdVersion', e.target.value)}
            placeholder={defaultEctdVersion()}
            style={inputStyle}
          />
        </label>

        {form.region === 'FDA' && (
          <>
            <label>
              <span style={labelStyle}>
                NDA number{isSupplement && <span style={requiredMark}>*</span>}
              </span>
              <input
                value={form.ndaNumber}
                onChange={(e) => update('ndaNumber', e.target.value)}
                disabled={lockedIdentity}
                onBlur={onIdentifierBlur}
                placeholder="6-digit"
                data-testid="application-identifier-input"
                style={inputStyle}
              />
            </label>
            <label>
              <span style={labelStyle}>Application pathway</span>
              <select
                disabled={lockedIdentity}
                value={form.applicationPathway}
                onChange={(e) => update('applicationPathway', e.target.value)}
                style={inputStyle}
              >
                <option value="">— Select —</option>
                {FDA_PATHWAYS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            {fdaSupplementEffectiveDateOptions(form.submissionTypeCode, form.submissionSubTypeCode).length > 0 && <label>
              <span style={labelStyle}>FDA supplement category *</span>
              <select value={form.supplementEffectiveDateType} style={inputStyle}
                aria-invalid={errors.supplementEffectiveDateType ? true : undefined}
                onChange={event => update('supplementEffectiveDateType', event.target.value)}>
                <option value="">— Select —</option>
                {fdaSupplementEffectiveDateOptions(form.submissionTypeCode, form.submissionSubTypeCode).map(code =>
                  <option key={code} value={code}>{FDA_SUPPLEMENT_EFFECTIVE_DATES[code as keyof typeof FDA_SUPPLEMENT_EFFECTIVE_DATES]}</option>)}
              </select>
              {errors.supplementEffectiveDateType && <span role="alert" style={{ color: 'var(--red)' }}>{errors.supplementEffectiveDateType}</span>}
            </label>}
            <label>
              <span style={labelStyle}>FDA activity starting sequence</span>
              <input value={form.fdaActivityStartSequence} inputMode="numeric" maxLength={4}
                aria-invalid={errors.fdaActivityStartSequence ? true : undefined}
                onChange={event => update('fdaActivityStartSequence', event.target.value)}
                placeholder="0000" style={inputStyle} />
              {errors.fdaActivityStartSequence && <span role="alert" style={{ color: 'var(--red)' }}>{errors.fdaActivityStartSequence}</span>}
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Needed before compilation: enter the sequence where this FDA activity began.</span>
            </label>
            <label>
              <span style={labelStyle}>Control number</span>
              <input
                value={form.controlNumber}
                onChange={(e) => update('controlNumber', e.target.value)}
                placeholder="Agency reference, if assigned"
                style={inputStyle}
              />
            </label>
          </>
        )}

        {(form.region === 'EMA' || form.region === 'MHRA') && (
          <>
            <label>
              <span style={labelStyle}>
                EU Application UUID{isSupplement && <span style={requiredMark}>*</span>}
              </span>
              <input
                value={form.euApplicationUuid}
                onChange={(e) => update('euApplicationUuid', e.target.value)}
                disabled={lockedIdentity}
                onBlur={onIdentifierBlur}
                data-testid="application-identifier-input"
                style={inputStyle}
              />
            </label>
            <label>
              <span style={labelStyle}>Procedure</span>
              <select
                disabled={lockedIdentity}
                value={form.applicationPathway}
                onChange={(e) => update('applicationPathway', e.target.value)}
                style={inputStyle}
              >
                <option value="">— Select —</option>
                {EMA_PROCEDURES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        {form.region === 'Health Canada' && (
          <>
            <label>
              <span style={labelStyle}>
                Dossier ID{isSupplement && <span style={requiredMark}>*</span>}
              </span>
              <input
                value={form.dossierId}
                onChange={(e) => update('dossierId', e.target.value)}
                disabled={lockedIdentity}
                onBlur={onIdentifierBlur}
                data-testid="application-identifier-input"
                style={inputStyle}
              />
            </label>
            <label>
              <span style={labelStyle}>Control number</span>
              <input
                value={form.controlNumber}
                onChange={(e) => update('controlNumber', e.target.value)}
                placeholder="6-digit"
                style={inputStyle}
              />
            </label>
          </>
        )}

        {(form.region === 'PMDA' || form.region === 'Swissmedic') && (
          <label>
            <span style={labelStyle}>
              Application number{isSupplement && <span style={requiredMark}>*</span>}
            </span>
            <input
              value={form.ndaNumber}
              onChange={(e) => update('ndaNumber', e.target.value)}
              disabled={lockedIdentity}
              onBlur={onIdentifierBlur}
              data-testid="application-identifier-input"
              style={inputStyle}
            />
          </label>
        )}

        {form.region === 'TGA' && (
          <label>
            <span style={labelStyle}>
              eSubmission ID{isSupplement && <span style={requiredMark}>*</span>}
            </span>
            <input
              value={form.dossierId}
              onChange={(e) => update('dossierId', e.target.value)}
              disabled={lockedIdentity}
              onBlur={onIdentifierBlur}
              data-testid="application-identifier-input"
              style={inputStyle}
            />
          </label>
        )}

        {/* T15-D8 — read-only: the server pins the specification at
            creation (SpecFileId is not a client-writable field), so the
            wizard shows exactly what it will pin instead of a picker whose
            choice was never sent. */}
        <div style={{ gridColumn: '1 / -1' }}>
          <span style={labelStyle}>Specification (pinned by the server at creation)</span>
          <div data-testid="wizard-spec-preview" style={{ fontSize: 13 }}>
            {specPreviewLine(form, specPreview)}
          </div>
          {form.filingType !== 'supplement' &&
            specPreview.state === 'resolved' &&
            specPreview.resolution.warning !== null && (
            <div role="alert" data-testid="wizard-spec-warning" style={errorStyle}>
              {specPreview.resolution.warning}
            </div>
          )}
        </div>

        {/* Phase phase-fix-new-submission-wizard-supplement (§3B/§3C) —
            identifier-lookup status. On the supplement path this reports the
            auto-detected next sequence (or the no-existing-application warning);
            on the original path it warns when the identifier already exists. */}
        {isSupplement && lookupStatus !== 'idle' && (
          <div
            data-testid="supplement-lookup-status"
            style={{
              gridColumn: '1 / -1',
              fontSize: 12,
              padding: '8px 12px',
              borderRadius: 6,
              background: lookupStatus === 'none' ? 'var(--amber-bg)' : 'var(--surface2)',
              color: lookupStatus === 'none' ? 'var(--amber)' : 'var(--text)',
            }}
          >
            {lookupStatus === 'loading' ? 'Looking up existing application…' : lookupMessage}
          </div>
        )}
        {/* feat/wizard-duplicate-sequence-guard — the old non-blocking
            "duplicate-dossier" warning is gone: an existing application on the
            original path is now a BLOCKING error under the sequence field
            (sequence-duplicate-error), with the supplement route offered. A
            lookup that could not run is reported here, honestly, and does not
            block — the server check still applies at Create. */}
        {uniquenessNotice && (
          <div
            data-testid="sequence-uniqueness-notice"
            style={{
              gridColumn: '1 / -1',
              fontSize: 12,
              padding: '8px 12px',
              borderRadius: 6,
              background: 'var(--amber-bg)',
              color: 'var(--amber)',
            }}
          >
            {uniquenessNotice}
          </div>
        )}
      </div>
    </fieldset>
  );
}

function Step3Sequence({ form, errors, update }: StepProps): ReactElement {
  return (
    <fieldset
      aria-labelledby="step-3-heading"
      style={{ border: 'none', padding: 0, margin: 0 }}
    >
      <h3 id="step-3-heading" style={{ margin: '0 0 12px 0' }}>
        Sequence details
      </h3>
      <div style={{ display: 'grid', gap: 16, gridTemplateColumns: '1fr 1fr' }}>
        <label>
          <span style={labelStyle}>Sequence purpose</span>
          <select
            value={form.sequencePurpose}
            onChange={(e) => update('sequencePurpose', e.target.value)}
            style={inputStyle}
          >
            <option value="">— Select —</option>
            {SEQUENCE_PURPOSE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span style={labelStyle}>Development phase</span>
          <select
            value={form.developmentPhase}
            onChange={(e) => update('developmentPhase', e.target.value)}
            style={inputStyle}
          >
            <option value="">— Select —</option>
            {DEVELOPMENT_PHASE_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>

        <label style={{ gridColumn: '1 / -1' }}>
          <span style={labelStyle}>
            Sequence description<span style={requiredMark}>*</span>
          </span>
          {form.region === 'Health Canada' ? (
            <>
              <select
                value={form.sequenceDescription}
                onChange={(e) => update('sequenceDescription', e.target.value)}
                aria-invalid={errors.sequenceDescription ? true : undefined}
                style={inputStyle}
              >
                <option value="">— Select sequence description —</option>
                {(form.submissionType
                  ? getHcSequenceDescriptionsForType(form.submissionType)
                  : HC_SEQUENCE_DESCRIPTIONS
                ).map((d) => (
                  <option key={d.code} value={d.label}>
                    {d.label}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <input
              value={form.sequenceDescription}
              onChange={(e) => update('sequenceDescription', e.target.value)}
              placeholder="e.g. Initial submission"
              aria-invalid={errors.sequenceDescription ? true : undefined}
              style={inputStyle}
            />
          )}
          {errors.sequenceDescription && (
            <div style={errorStyle}>{errors.sequenceDescription}</div>
          )}
        </label>
      </div>
    </fieldset>
  );
}

interface Step4ReviewProps {
  readonly form: WizardFormState;
  readonly companies: CompanyProfile[] | null;
  readonly specPreview: SpecPreview;
  /** Phase KB-submission-types-v3 — catalog keyed by applicationType, used to
   *  resolve the submission type/sub-type CODES to their friendly labels. */
  readonly catalogByAppType: Map<string, SubmissionTypeGroup[]>;
}

function Step4Review({ form, companies, specPreview, catalogByAppType }: Step4ReviewProps): ReactElement {
  const companyName =
    form.companyProfileId && companies
      ? (companies.find((c) => c.Id === form.companyProfileId)?.CompanyName ?? form.companyProfileId)
      : '(none)';

  // phase-ui-review-F11 — the review showed the raw code ("fdast1") where a
  // person expects the label ("Original Application"). Resolve through the same
  // catalog the dropdowns use; show the label with the code as a secondary
  // detail, and fall back to the bare code only if the catalog hasn't loaded.
  const submissionTypeGroup = form.applicationType
    ? (catalogByAppType.get(form.applicationType) ?? []).find((t) => t.code === form.submissionTypeCode)
    : undefined;
  const submissionTypeLabel = submissionTypeGroup
    ? `${submissionTypeGroup.name} (${submissionTypeGroup.code})`
    : form.submissionTypeCode || '—';
  const subTypeMatch = submissionTypeGroup?.subTypes.find((st) => st.code === form.submissionSubTypeCode);
  const subTypeLabel = subTypeMatch
    ? `${subTypeMatch.name} (${subTypeMatch.code})`
    : form.submissionSubTypeCode || '—';

  const rows: Array<[string, string]> = [
    ['Drug name', form.drugName],
    ['Company', companyName],
    ['INN', form.innName || '—'],
    ['Therapeutic area', form.therapeuticArea || '—'],
    ['Indication', form.indication || '—'],
    ['Region', `${REGION_FLAGS[form.region]} ${form.region}`],
    ['Application type', form.applicationType || form.submissionType || '—'],
    ['Submission type', submissionTypeLabel],
    ['Sub-type', subTypeLabel],
    ['Sequence number', form.sequenceNumber],
    ['eCTD version', form.ectdVersion || '—'],
  ];
  if (form.region === 'FDA') {
    rows.push(['NDA number', form.ndaNumber || '—']);
    rows.push(['Application pathway', form.applicationPathway || '—']);
    rows.push(['Control number', form.controlNumber || '—']);
  } else if (form.region === 'EMA' || form.region === 'MHRA') {
    rows.push(['EU Application UUID', form.euApplicationUuid || '—']);
    rows.push(['Procedure', form.applicationPathway || '—']);
  } else if (form.region === 'Health Canada') {
    rows.push(['Dossier ID', form.dossierId || '—']);
    rows.push(['Control number', form.controlNumber || '—']);
  } else if (form.region === 'PMDA' || form.region === 'Swissmedic') {
    rows.push(['Application number', form.ndaNumber || '—']);
  } else if (form.region === 'TGA') {
    rows.push(['eSubmission ID', form.dossierId || '—']);
  }
  rows.push(['Specification', specPreviewLine(form, specPreview)]);
  if (form.region === 'FDA' && form.supplementEffectiveDateType) rows.push(['FDA supplement category', FDA_SUPPLEMENT_EFFECTIVE_DATES[form.supplementEffectiveDateType as keyof typeof FDA_SUPPLEMENT_EFFECTIVE_DATES]]);
  if (form.region === 'FDA') rows.push(['FDA activity starting sequence', form.fdaActivityStartSequence || 'Needed before compilation']);
  rows.push(['Sequence purpose', form.sequencePurpose || '—']);
  rows.push(['Sequence description', form.sequenceDescription]);
  rows.push(['Development phase', form.developmentPhase || '—']);

  return (
    <fieldset
      aria-labelledby="step-4-heading"
      style={{ border: 'none', padding: 0, margin: 0 }}
    >
      <h3 id="step-4-heading" style={{ margin: '0 0 12px 0' }}>
        Review &amp; create
      </h3>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 0 }}>
        Confirm the details below, then click <strong>Create submission</strong>. You can go back
        to any prior step to edit.
      </p>
      <table
        data-testid="review-table"
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
          border: '1px solid var(--border)',
          borderRadius: 6,
        }}
      >
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k} style={{ borderBottom: '1px solid var(--border)' }}>
              <th
                scope="row"
                style={{
                  textAlign: 'left',
                  padding: '8px 12px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  width: 200,
                  background: 'var(--surface2)',
                }}
              >
                {k}
              </th>
              <td style={{ padding: '8px 12px', color: 'var(--text)' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </fieldset>
  );
}

// ----- Page -----

export function NewSubmissionPage(): ReactElement {
  const navigate = useNavigate();
  const workspace = useSubmissionWorkspace();
  useEffect(() => () => { if (workspace) workspace.dirty.current = false; }, [workspace]);
  const [step, setStep] = useState<WizardStep>(0);
  const [form, setForm] = useState<WizardFormState>(initialForm);
  // phase-fix-new-supplement-routing — supplement-of-a-specific-application
  // mode is carried in the URL (?supplementOf=<id>) so a mid-wizard refresh
  // preserves it. When set: prefill + lock the parent's identity, force the
  // supplement filing path, and resolve the next sequence + linear parent.
  // An unresolvable id FAILS LOUD (redirect back) — never silently degrades
  // to the generic new-application wizard.
  const [searchParams] = useSearchParams();
  const supplementOf = searchParams.get('supplementOf');
  const [supplementParentId, setSupplementParentId] = useState<string | null>(null);
  const [supplementError, setSupplementError] = useState<string | null>(null);

  // Refs always hold the latest step/form so that goNext never reads stale
  // closure values — important for tests where multiple act() cycles fire back-to-back.
  const stepRef = useRef<WizardStep>(step);
  const formRef = useRef<WizardFormState>(form);
  stepRef.current = step;
  formRef.current = form;

  const [companies, setCompanies] = useState<CompanyProfile[] | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [specPreview, setSpecPreview] = useState<SpecPreview>({ state: 'loading' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  // Phase KB-submission-types-v3 — catalog state
  const [catalogByAppType, setCatalogByAppType] = useState<Map<string, SubmissionTypeGroup[]>>(
    new Map(),
  );
  const [catalogAppTypes, setCatalogAppTypes] = useState<string[]>([]);
  // Phase phase-ui-consistency-sweep (§3D / TODO-120) — start in the
  // loading state so the application-type dropdown shows "Loading…" on
  // first paint instead of "— None available —" (which implied a dead
  // end before the catalog fetch had even run).
  const [catalogLoading, setCatalogLoading] = useState(true);

  // Phase 7.6 — parallel mode: create one submission per region
  const [parallelMode, setParallelMode] = useState(false);
  const [additionalRegions, setAdditionalRegions] = useState<string[]>([]);
  // claude-regional-fixes (issue 3) — each additional region's EXPLICIT setup
  // (application type, activity code, optional sub-type, market identifier).
  // Nothing is copied from the primary region; the user supplies each market's
  // own values. Keyed by region display value.
  const [regionSetups, setRegionSetups] = useState<Record<string, ParallelRegionDraft>>({});
  const parallelStartInvalid = parallelMode && additionalRegions.some(region => parallelActivityStartError(region, regionSetups[region]) !== null);
  const parallelStartInvalidRef = useRef(parallelStartInvalid);
  parallelStartInvalidRef.current = parallelStartInvalid;
  useEffect(() => {
    setAdditionalRegions(previous => previous.filter(region => region !== form.region));
  }, [form.region]);

  // Phase phase-fix-new-submission-wizard-supplement — existing-application
  // lookup state for the supplement path (auto-sequence) + original path
  // (duplicate-dossier guard).
  const [lookupStatus, setLookupStatus] = useState<'idle' | 'loading' | 'found' | 'none'>('idle');
  const [lookupMessage, setLookupMessage] = useState<string | null>(null);
  // feat/wizard-duplicate-sequence-guard — Submission-step duplicate check.
  // `dupReqRef` numbers requests so a stale response never overwrites a newer
  // one; `serverDupKeyRef` pins a server-reported 409 to the inputs it was
  // reported for, so the debounced re-check does not clear it until the
  // operator actually changes identifier / region / sequence.
  const [dupCheck, setDupCheck] = useState<DuplicateCheck>({ status: 'idle' });
  const dupReqRef = useRef(0);
  const completedDupCheckRef = useRef<{ request: number; key: string; result: DuplicateCheck } | null>(null);
  const serverDupKeyRef = useRef<string | null>(null);
  // feat/wizard-duplicate-sequence-guard — reentrancy guard for the async
  // Submission-step advance: `goNext` awaits a fresh duplicate lookup, so a
  // second click while the first is in flight must be ignored — otherwise two
  // overlapping advances skip a step and a stale (superseded) check advances.
  const advancingRef = useRef(false);

  const validation = useMemo(() => validateStep(step, form), [step, form]);
  const visibleErrors = showErrors ? validation.errors : {};
  useEffect(() => {
    if (showErrors) document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [showErrors, step]);

  const update = useCallback(
    <K extends keyof WizardFormState>(key: K, value: WizardFormState[K]): void => {
      setForm((prev) => {
        const next = { ...prev, [key]: value };
        if (['region', 'applicationType', 'submissionTypeCode', 'submissionSubTypeCode'].includes(key) && value !== prev[key]) next.supplementEffectiveDateType = '';
        if (key === 'region' && value !== prev.region) {
          const r = value as Region;
          next.submissionType = '';
          next.applicationType = '';
          next.submissionTypeCode = '';
          next.submissionSubTypeCode = '';
          next.applicationPathway = '';
          next.ndaNumber = '';
          next.controlNumber = '';
          next.fdaActivityStartSequence = '';
          next.euApplicationUuid = '';
          next.dossierId = '';
          next.ectdVersion = defaultEctdVersion();
          // Phase phase-fix-new-submission-wizard-supplement — the application
          // identifier (and therefore the existing-application lookup + parent
          // link) is region-specific; clear it when the region changes.
          next.parentSubmissionId = '';
          // An untouched regional default follows the region (FDA 0001 → EMA 0000).
          if (prev.filingType === 'original'
            || (prev.filingType === '' && prev.sequenceNumber === initialSequenceNumberForRegion(prev.region))) {
            next.sequenceNumber = initialSequenceNumberForRegion(r);
          }
        }
        if (key === 'submissionType' && !prev.sequenceDescription) {
          const seed = defaultSequenceDescription(value as string);
          if (seed) next.sequenceDescription = seed;
        }
        return next;
      });
      if (key === 'region') {
        setLookupStatus('idle');
        setLookupMessage(null);
        serverDupKeyRef.current = null;
        setDupCheck({ status: 'idle' });
      }
      setShowErrors(false);
    },
    [],
  );

  useEffect(() => {
    if (!supplementOf) {
      setSupplementParentId(null);
      return;
    }
    let cancelled = false;
    setSupplementError(null);
    void (async (): Promise<void> => {
      try {
        // The named application, then its whole sequence family (for the
        // linear parent = highest existing sequence, and next = max+1).
        const source = await v2.submissions.get(supplementOf);
        const region = source.Region;
        const nda = source.NdaNumber ?? '';
        const euUuid = source.EuApplicationUuid ?? '';
        const dossier = source.DossierId ?? '';
        const identValue = nda || euUuid || dossier;
        let family: Submission[] = [source];
        if (identValue !== '') {
          const matches = (
            await v2.submissions.list({
              region,
              search: identValue,
              includeArchived: true,
              limit: 200,
            })
          ).data.filter((r) => {
            const rv =
              (r.NdaNumber ?? '').trim() ||
              (r.EuApplicationUuid ?? '').trim() ||
              (r.DossierId ?? '').trim();
            return rv === identValue;
          });
          // Fall back to the source alone if the identifier search over-narrows
          // (never leave the family empty — that crashed the parent pick).
          if (matches.length > 0) family = matches;
        }
        if (cancelled) return;
        const parent =
          [...family].sort(
            (a, b) => parseSeq(b.SequenceNumber) - parseSeq(a.SequenceNumber),
          )[0] ?? source;
        const nextSeq = nextSequenceNumber(family);
        setForm((prev) => {
          // claude-regional-fixes (issue 4) — a supplement is a new sequence of
          // the SAME application, so it must inherit the application's stable
          // identity and metadata, not reset them. The old prefill copied only
          // the identifiers, dropping the INN and (critically) the application
          // type — forcing a re-pick that could silently diverge from the
          // application's real type. Inherit the stable, application-level
          // fields here; the deliberate per-sequence choices (activity code,
          // sub-type, sequence description/purpose) are left for the user.
          const inheritedAppType = source.SubmissionType ?? prev.applicationType;
          return {
            ...prev,
            filingType: 'supplement',
            drugName: source.DrugName,
            region,
            companyProfileId: source.CompanyProfileId ?? prev.companyProfileId,
            innName: source.InnName ?? prev.innName,
            // ApplicationType is an application-level property, identical across
            // every sequence. Mirror it into both the catalog field and the
            // legacy free-text field the payload falls back to.
            applicationType: inheritedAppType,
            submissionType: inheritedAppType,
            therapeuticArea: source.TherapeuticArea ?? prev.therapeuticArea,
            indication: source.Indication ?? prev.indication,
            developmentPhase: source.DevelopmentPhase ?? prev.developmentPhase,
            applicationPathway: source.ApplicationPathway ?? prev.applicationPathway,
            ectdVersion: source.EctdVersion ?? prev.ectdVersion,
            ndaNumber: nda,
            euApplicationUuid: euUuid,
            dossierId: dossier,
            controlNumber: source.ControlNumber ?? prev.controlNumber,
            sequenceNumber: nextSeq,
            parentSubmissionId: parent.Id,
          };
        });
        setSupplementParentId(parent.Id);
        setLookupStatus('found');
        setLookupMessage(
          `Supplement to ${source.DrugName} (${region}) — ${family.length} existing ` +
            `sequence${family.length === 1 ? '' : 's'}, highest ${parent.SequenceNumber}. ` +
            `New sequence ${nextSeq}.`,
        );
      } catch (err) {
        if (cancelled) return;
        // FAIL LOUD — the source application isn't resolvable (deleted /
        // hidden / bad id). Do not degrade to the generic wizard.
        setSupplementParentId(null);
        setSupplementError(
          err instanceof ApiError && err.status === 404
            ? 'That application could not be found — it may have been removed. Start a supplement from its Submission Details page.'
            : 'Could not load the source application for this supplement. Please try again from its Submission Details page.',
        );
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, [supplementOf]);

  useEffect(() => {
    let cancelled = false;
    v2.companyProfiles
      .list()
      .then((rows) => {
        if (cancelled) return;
        setCompanies(rows);
        if (rows.length === 1 && rows[0]) {
          const only = rows[0];
          setForm((prev) =>
            prev.companyProfileId ? prev : { ...prev, companyProfileId: only.Id },
          );
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCompanyError(err instanceof ApiError ? err.message : 'Failed to load company profiles');
        setCompanies([]);
      });
    return (): void => {
      cancelled = true;
    };
  }, []);

  // T15-D8 — ask the server which specification creation will pin for this
  // region + eCTD generation (the same resolver POST /submissions calls).
  useEffect(() => {
    let cancelled = false;
    setSpecPreview({ state: 'loading' });
    v2.specFiles
      .resolve(form.region, form.ectdVersion)
      .then((resolution) => {
        if (!cancelled) setSpecPreview({ state: 'resolved', resolution });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setSpecPreview({
          state: 'error',
          message: `Could not resolve the specification (${err instanceof ApiError ? err.message : 'request failed'})`,
        });
      });
    return (): void => {
      cancelled = true;
    };
  }, [form.region, form.ectdVersion]);

  // Phase KB-submission-types-v3 — load submission type catalog when region changes.
  // First fetch returns distinct application types for the region; then we fetch
  // per-appType to get grouped submission types for the cascading dropdowns.
  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogAppTypes([]);
    setCatalogByAppType(new Map());
    v2.submissionTypes
      .list({ region: form.region })
      .then((res) => {
        if (cancelled) return;
        // apiFetch already unwraps { ok, data } — read the payload directly.
        // Reading `res.data.…` here threw a TypeError that the .catch() below
        // swallowed, leaving the dropdown permanently empty for every region.
        const appTypesList = res.applicationTypes;
        setCatalogAppTypes(appTypesList);
        // Fetch per-applicationType groups (parallel) for cascading dropdown.
        const fetches = appTypesList.map((at) =>
          v2.submissionTypes.list({ region: form.region, applicationType: at }).then((r) => ({
            at,
            types: r.submissionTypes,
          })),
        );
        Promise.all(fetches)
          .then((results) => {
            if (cancelled) return;
            const map = new Map<string, SubmissionTypeGroup[]>();
            for (const { at, types } of results) {
              map.set(at, types);
            }
            setCatalogByAppType(map);
          })
          .catch(() => {
            // Non-critical: catalog dropdowns fall back to empty
          })
          .finally(() => {
            if (!cancelled) setCatalogLoading(false);
          });
      })
      .catch(() => {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      });
    return (): void => {
      cancelled = true;
    };
  }, [form.region]);

  // Shared identifier lookup — returns existing submissions in the same tenant
  // whose region + application identifier match the form. Uses the list
  // endpoint's server-side `search` + `region` filters, then narrows to an
  // EXACT identifier match (search is a LIKE, so it can over-match).
  // feat/wizard-duplicate-sequence-guard — takes the form it is fetching FOR
  // (defaulting to the live ref for the blur caller). The duplicate check pins
  // a snapshot and passes it here, so the returned matches always belong to the
  // identifier/region the result will be scored against — never a later edit.
  const fetchExistingForIdentifier = useCallback(
    async (form: WizardFormState = formRef.current): Promise<Submission[] | null> => {
      const id = applicationIdentifier(form);
      if (!id || id.value === '') return null;
      const { data } = await v2.submissions.list({
        region: form.region,
        search: id.value,
        includeArchived: true,
        limit: 200,
      });
      return data.filter((s) => String(s[id.subField] ?? '').trim() === id.value);
    },
    [],
  );

  // feat/wizard-duplicate-sequence-guard — the "sequenceTaken" check. Looks
  // up the application (same tenant, same region, archived included) and
  // evaluates the form against it. Numbered so a stale response is dropped.
  // A lookup failure is reported as 'error' and never blocks.
  const runDuplicateCheck = useCallback(async (): Promise<DuplicateCheck> => {
    // Pin the form this check is FOR. Everything downstream — the fetch, the
    // supersede guard, and the pure evaluation — is tied to THIS snapshot, so a
    // late response can never be scored against a form the operator has since
    // edited (identifier / region / sequence). Evaluating new-form inputs
    // against old-identifier matches was the stale-result bug.
    const snapshot = formRef.current;
    const key = duplicateCheckKey(snapshot);
    const id = applicationIdentifier(snapshot);
    if (!id || id.value === '') {
      setDupCheck({ status: 'idle' });
      return { status: 'idle' };
    }
    const my = ++dupReqRef.current;
    setDupCheck((prev) => (prev.status === 'found' && prev.fromServer ? prev : { status: 'loading' }));
    // T07-D16 — two different kinds of "superseded":
    //  - the identity changed under us (an edit within the debounce window
    //    never bumps the request counter): the result is stale for everyone,
    //    so it resolves to 'loading';
    //  - a NEWER check for the SAME identity started: a pending check does
    //    not discard the caller's result, but a completed newer result must
    //    govern both the warning and navigation.
    // Treating the second like the first made goNext swallow a Next click
    // silently: no advance, no validation, no error.
    const identityChanged = (): boolean => duplicateCheckKey(formRef.current) !== key;
    const isNewest = (): boolean => my === dupReqRef.current;
    const complete = (result: DuplicateCheck): DuplicateCheck => {
      const latest = completedDupCheckRef.current;
      if (latest && latest.key === key && latest.request > my) {
        // An error supplies no newer identity evidence. Preserve an actual
        // collision even when this older request completes after that error.
        if (latest.result.status === 'error' && result.status === 'found') {
          completedDupCheckRef.current = { ...latest, result };
          setDupCheck(result);
          return result;
        }
        return latest.result;
      }
      // The opposite completion order has the same rule: a failed refresh
      // cannot clear a known collision. A successful newer 'none' still can,
      // and a standalone lookup error remains non-blocking.
      const governing = result.status === 'error' && latest?.key === key && latest.result.status === 'found'
        ? latest.result : result;
      if (!latest || my > latest.request) completedDupCheckRef.current = { request: my, key, result: governing };
      if (isNewest()) setDupCheck(governing);
      return governing;
    };
    try {
      const matches = await fetchExistingForIdentifier(snapshot);
      if (identityChanged()) return { status: 'loading' };
      const result = evaluateDuplicate(snapshot, matches ?? []);
      return complete(result);
    } catch {
      if (identityChanged()) return { status: 'loading' };
      const result: DuplicateCheck = {
        status: 'error',
        message:
          "Couldn't verify uniqueness — the lookup failed. You can continue; the server will still " +
          'refuse a duplicate sequence at Create.',
      };
      return complete(result);
    }
  }, [fetchExistingForIdentifier]);

  // Re-run on the Submission step whenever identifier, region, filing type or
  // sequence changes (debounced ~300 ms). A server-reported duplicate stays
  // pinned until one of those inputs changes.
  const dupKey = duplicateCheckKey(form);
  useEffect(() => {
    if (step !== 1) return;
    if (serverDupKeyRef.current !== null && serverDupKeyRef.current === dupKey) return;
    serverDupKeyRef.current = null;
    const id = applicationIdentifier(form);
    if (!id || id.value === '') {
      setDupCheck({ status: 'idle' });
      return;
    }
    const t = window.setTimeout(() => {
      void runDuplicateCheck();
    }, 300);
    return () => window.clearTimeout(t);
    // `dupKey` captures region + identifier + filingType + sequence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, dupKey, runDuplicateCheck]);

  const applyNextSequence = useCallback((): void => {
    if (dupCheck.status !== 'found') return;
    // feat/wizard-duplicate-sequence-guard — never blank the sequence field:
    // a server 409 may omit a next sequence (older SequenceCollision shape),
    // leaving nextSequence ''. Only apply a real, parseable value.
    const next = normalizeSeq(dupCheck.nextSequence);
    if (!next) return;
    serverDupKeyRef.current = null;
    setForm((prev) => ({ ...prev, sequenceNumber: next }));
  }, [dupCheck]);

  // Read from refs so goNext always validates against the committed state,
  // regardless of which render the callback was created during.
  const goNext = useCallback(async (): Promise<void> => {
    // One advance at a time: ignore a click while an async Submission-step
    // check is already running, so double clicks cannot stack two advances.
    if (advancingRef.current) return;
    const stepAtStart = stepRef.current;
    const v = validateStep(stepAtStart, formRef.current);
    if (!v.ok || (stepAtStart === 1 && parallelStartInvalidRef.current)) {
      setShowErrors(true);
      return;
    }
    // feat/wizard-duplicate-sequence-guard — on the Submission step the
    // duplicate check runs AGAIN here, fresh, so a fast click cannot slip past
    // the debounced check. A lookup failure does not block (the server check
    // at Create still applies); only a confirmed duplicate does.
    if (stepAtStart === 1) {
      // Pin the step + identity this advance is FOR. After the await we re-read
      // both from the refs: if the operator navigated away/back (step changed)
      // or edited identifier / region / sequence (key changed) while the lookup
      // was in flight, the resolved result — success OR error — is stale and
      // must NOT advance. The next click (or the fresh debounced check) decides.
      const keyAtStart = duplicateCheckKey(formRef.current);
      advancingRef.current = true;
      try {
        const result = await runDuplicateCheck();
        if (stepRef.current !== stepAtStart) return; // navigated during the check
        if (duplicateCheckKey(formRef.current) !== keyAtStart) return; // identity edited
        if (result.status === 'found') {
          setShowErrors(true);
          return;
        }
        // 'loading' means the identity changed under the check (T07-D16): never
        // advance against a stale result — the next click decides. A newer
        // check for the SAME identity no longer turns this result into
        // 'loading'. Only 'none' / 'idle' / 'error' (non-blocking) advance.
        if (result.status === 'loading') return;
        // Required fields may have changed during the lookup (a non-identity
        // edit does not change the key): revalidate the current form before
        // advancing, so a lookup can never carry an invalid form forward.
        const recheck = validateStep(stepRef.current, formRef.current);
        if (!recheck.ok || parallelStartInvalidRef.current) {
          setShowErrors(true);
          return;
        }
      } finally {
        advancingRef.current = false;
      }
    }
    setShowErrors(false);
    setStep((s) => (s < 3 ? ((s + 1) as WizardStep) : s));
  }, [runDuplicateCheck]);

  const goBack = useCallback((): void => {
    setShowErrors(false);
    setStep((s) => (s > 0 ? ((s - 1) as WizardStep) : s));
  }, []);

  // Phase KB-submission-types-v3 — batched Application-type change so that all
  // four related fields are written in a single setState call.  This ensures
  // that `goNext` (which closes over `form`) sees the fully-updated form state
  // on the very next re-render instead of requiring an extra render cycle.
  const handleAppTypeChange = useCallback((val: string): void => {
    setForm((prev) => {
      const next: WizardFormState = {
        ...prev,
        applicationType: val,
        submissionType: val,
        submissionTypeCode: '',
        submissionSubTypeCode: '',
      };
      if (!prev.sequenceDescription && val) {
        const seed = defaultSequenceDescription(val);
        if (seed) next.sequenceDescription = seed;
      }
      return next;
    });
    setShowErrors(false);
  }, []);

  // Phase phase-fix-new-submission-wizard-supplement (§3A) — original/supplement
  // gate. Original locks the sequence to the regional initial number (FDA/TGA 0001, else 0000) and drops any parent link;
  // supplement leaves the sequence editable until the identifier lookup runs.
  // The level-2 submission-type code is cleared because the type list re-filters
  // by original-vs-supplement when the gate flips.
  const handleFilingTypeChange = useCallback((val: FilingType): void => {
    setForm((prev) => {
      const next: WizardFormState = {
        ...prev,
        filingType: val,
        submissionTypeCode: '',
        submissionSubTypeCode: '',
        parentSubmissionId: '',
      };
      if (val === 'original') next.sequenceNumber = initialSequenceNumberForRegion(prev.region);
      return next;
    });
    setLookupStatus('idle');
    setLookupMessage(null);
    serverDupKeyRef.current = null;
    setDupCheck({ status: 'idle' });
    setShowErrors(false);
  }, []);

  // Shared identifier lookup — returns existing submissions in the same tenant
  // whose region + application identifier match the form. Uses the list
  // endpoint's server-side `search` + `region` filters, then narrows to an
  // EXACT identifier match (search is a LIKE, so it can over-match).
  // (feat/wizard-duplicate-sequence-guard — `fetchExistingForIdentifier` now
  // lives above `goNext`, next to the duplicate check that shares it.)

  // Phase phase-fix-new-submission-wizard-supplement (§3B/§3C) — fired on blur
  // of the region's application-identifier field. Supplement path: auto-detect
  // the next sequence (max+1) + link the parent. Original path: warn if the
  // identifier already exists (duplicate-dossier guard).
  const handleIdentifierBlur = useCallback((): void => {
    const filing = formRef.current.filingType;
    const id = applicationIdentifier(formRef.current);
    if (filing === '' || !id || id.value === '') return;

    if (filing === 'supplement') {
      setLookupStatus('loading');
      setLookupMessage(null);
      void fetchExistingForIdentifier()
        .then((matches) => {
          if (matches === null) {
            setLookupStatus('idle');
            return;
          }
          if (matches.length === 0) {
            setLookupStatus('none');
            setLookupMessage(
              'No existing application found with this identifier. If this is truly a new ' +
                "original application, go back and select 'No — new original application'.",
            );
            return;
          }
          const parent = [...matches].sort(
            (a, b) => parseSeq(b.SequenceNumber) - parseSeq(a.SequenceNumber),
          )[0]!;
          const nextSeq = nextSequenceNumber(matches);
          setForm((prev) => ({ ...prev, sequenceNumber: nextSeq, parentSubmissionId: parent.Id }));
          setLookupStatus('found');
          setLookupMessage(
            `Found ${matches.length} existing sequence${matches.length === 1 ? '' : 's'} ` +
              `(highest ${parent.SequenceNumber}). Next sequence auto-detected: ${nextSeq}.`,
          );
        })
        .catch(() => {
          setLookupStatus('idle');
          setLookupMessage(
            'Could not look up existing applications. Enter the sequence number manually.',
          );
        });
      return;
    }

    // Original path — feat/wizard-duplicate-sequence-guard: the former soft
    // "duplicate-dossier" warning is superseded by the BLOCKING duplicate
    // check (debounced on every identifier/region/sequence change, and again
    // in goNext). Nothing to do on blur beyond that.
  }, [fetchExistingForIdentifier]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    for (let s = 0 as WizardStep; s < 3; s = (s + 1) as WizardStep) {
      const v = validateStep(s, form);
      if (!v.ok || (s === 1 && parallelStartInvalid)) {
        setStep(s);
        setShowErrors(true);
        return;
      }
    }
    setSubmitting(true);
    setSubmitError(null);
    const payload: Record<string, unknown> = {
      DrugName: form.drugName.trim(),
      Region: form.region,
      SubmissionType: form.applicationType || form.submissionType,
      SequenceNumber: form.sequenceNumber.trim() || initialSequenceNumberForRegion(form.region),
      SequenceDescription: form.sequenceDescription.trim(),
    };
    // Phase KB-submission-types-v3 — include catalog codes when selected.
    if (form.submissionTypeCode) payload['SubmissionTypeCode'] = form.submissionTypeCode;
    if (form.submissionSubTypeCode) payload['SubmissionSubTypeCode'] = form.submissionSubTypeCode;
    if (form.companyProfileId) payload['CompanyProfileId'] = form.companyProfileId;
    if (form.innName.trim()) payload['InnName'] = form.innName.trim();
    if (form.therapeuticArea.trim()) payload['TherapeuticArea'] = form.therapeuticArea.trim();
    if (form.indication.trim()) payload['Indication'] = form.indication.trim();
    if (form.ectdVersion.trim()) payload['EctdVersion'] = form.ectdVersion.trim();
    if (form.ndaNumber.trim()) payload['NdaNumber'] = form.ndaNumber.trim();
    if (form.applicationPathway) payload['ApplicationPathway'] = form.applicationPathway;
    if (form.controlNumber.trim()) payload['ControlNumber'] = form.controlNumber.trim();
    if (form.region === 'FDA' && form.supplementEffectiveDateType) payload['SupplementEffectiveDateType'] = form.supplementEffectiveDateType;
    if (form.region === 'FDA' && form.fdaActivityStartSequence.trim()) payload['FdaActivityStartSequence'] = form.fdaActivityStartSequence.trim();
    if (form.euApplicationUuid.trim()) payload['EuApplicationUuid'] = form.euApplicationUuid.trim();
    if (form.dossierId.trim()) payload['DossierId'] = form.dossierId.trim();
    if (form.sequencePurpose) payload['SequencePurpose'] = form.sequencePurpose;
    if (form.developmentPhase) payload['DevelopmentPhase'] = form.developmentPhase;
    // Phase phase-fix-new-submission-wizard-supplement (§3B) — link supplements
    // to the parent application so backend lifecycle / prior-sequence logic can
    // see the chain. Already whitelisted in ALLOWED_WRITE_FIELDS.
    if (form.parentSubmissionId) payload['ParentSubmissionId'] = form.parentSubmissionId;
    try {
      if (parallelMode && additionalRegions.length > 0) {
        // claude-regional-fixes (issue 3) — build the per-region payload. The
        // group shares product/company metadata only; every region (primary +
        // each additional) carries its OWN application type, activity code and
        // market identifier. Nothing is copied across markets.
        const shared: ParallelSharedMetadata = { DrugName: form.drugName.trim() };
        if (form.innName.trim()) shared.InnName = form.innName.trim();
        if (form.indication.trim()) shared.Indication = form.indication.trim();
        if (form.therapeuticArea.trim()) shared.TherapeuticArea = form.therapeuticArea.trim();
        if (form.companyProfileId) shared.CompanyProfileId = form.companyProfileId;
        if (form.ectdVersion.trim()) shared.EctdVersion = form.ectdVersion.trim();
        if (form.developmentPhase) shared.DevelopmentPhase = form.developmentPhase;

        // The primary region comes from the main wizard form. Its identifier
        // field is chosen by the canonical evaluator; an unrecognised region
        // carries no application identifier rather than a guessed one.
        const primaryId = applicationIdentifier(form);
        const primary: ParallelRegionSetup = {
          Region: form.region,
          SubmissionType: form.applicationType || form.submissionType,
          SubmissionTypeCode: form.submissionTypeCode,
        };
        if (primaryId) primary[primaryId.subField as keyof ParallelRegionSetup] = primaryId.value;
        if (form.sequenceNumber.trim()) primary.SequenceNumber = form.sequenceNumber.trim();
        if (form.submissionSubTypeCode) primary.SubmissionSubTypeCode = form.submissionSubTypeCode;
        if (form.sequenceDescription.trim()) primary.SequenceDescription = form.sequenceDescription.trim();
        if (form.applicationPathway) primary.ApplicationPathway = form.applicationPathway;
        if (form.controlNumber.trim()) primary.ControlNumber = form.controlNumber.trim();
        if (form.region === 'FDA' && form.supplementEffectiveDateType) primary.SupplementEffectiveDateType = form.supplementEffectiveDateType;
        if (form.region === 'FDA' && form.fdaActivityStartSequence.trim()) primary.FdaActivityStartSequence = form.fdaActivityStartSequence.trim();
        if (form.parentSubmissionId) primary.ParentSubmissionId = form.parentSubmissionId;

        // Additional regions come from their explicit per-region setups. Block
        // the submit (and highlight the cards) if any is incomplete rather than
        // sending a request the server will reject.
        const incomplete = additionalRegions.filter((r) => {
          const d = regionSetups[r];
          return !d || !d.applicationType || !d.submissionTypeCode || d.identifier.trim() === '';
        });
        if (incomplete.length > 0) {
          setSubmitting(false);
          setShowErrors(true);
          setStep(1);
          setSubmitError(
            `Complete the setup for: ${incomplete.join(', ')} (application type, submission type and identifier).`,
          );
          return;
        }
        const additional: ParallelRegionSetup[] = additionalRegions.map((r) => {
          const d = regionSetups[r] ?? emptyRegionDraft();
          const idSpec = identifierSpecForRegion(r);
          const setup: ParallelRegionSetup = {
            Region: r,
            SubmissionType: d.applicationType,
            SubmissionTypeCode: d.submissionTypeCode,
          };
          if (idSpec) setup[idSpec.key] = d.identifier.trim();
          if (r === 'FDA' && d.fdaActivityStartSequence?.trim()) setup.FdaActivityStartSequence = d.fdaActivityStartSequence.trim();
          if (d.submissionSubTypeCode) setup.SubmissionSubTypeCode = d.submissionSubTypeCode;
          return setup;
        });

        const result = await v2.submissions.createParallel({
          shared,
          regions: [primary, ...additional],
        });
        if (workspace) workspace.dirty.current = false;
        navigate(`/submissions/${result.submissionIds[0] ?? ''}`);
      } else {
        const created = (await v2.submissions.create(payload)) as Submission;
        if (workspace) workspace.dirty.current = false;
        navigate(`/submissions/${created.Id}`);
      }
    } catch (err) {
      // feat/wizard-duplicate-sequence-guard — final safety net. A 409 from
      // create / createParallel (DuplicateSequence with code DUPLICATE_SEQUENCE,
      // or the older SequenceCollision) is a Submission-step problem: go back
      // to that step and show the same blocking sequence error the client
      // check uses, pinned to the inputs it was reported for.
      if (err instanceof ApiError && err.status === 409) {
        const body = parseApiErrorBody(err) as
          | {
              error?: string;
              code?: string;
              message?: string;
              details?: { existingId?: string; nextSequenceNumber?: string };
            }
          | null;
        setSubmitting(false);
        setSubmitError(null);
        serverDupKeyRef.current = duplicateCheckKey(form);
        setDupCheck({
          status: 'found',
          original: form.filingType === 'original',
          message: describeApiError(err, 'This sequence already exists for this application.').message,
          nextSequence: body?.details?.nextSequenceNumber ?? '',
          supplementOfId: body?.details?.existingId ?? null,
          fromServer: true,
        });
        setStep(1);
        setShowErrors(true);
        return;
      }
      setSubmitError(describeApiError(err, 'Failed to create submission').message);
      setSubmitting(false);
    }
  }, [form, navigate, parallelMode, additionalRegions, regionSetups, workspace, parallelStartInvalid]);

  const isLastStep = step === 3;

  return (
    <section onChangeCapture={() => { if (workspace) workspace.dirty.current = true; }}>
      {specPreview.state === 'resolved' && <div style={{ marginBottom: 16, padding: 12, border: '1px solid var(--border)', borderRadius: 6 }}><CompilerProfileSummary profile={specPreview.resolution.compilerProfile ?? null} /></div>}
      {/* phase-v1-ui-parity — V1 NewSubmissionScreen: "New Submission"
          (capital S) via .screen-title; the step label sits in .screen-subtitle.
          phase-home-milestones follow-up (operator request, 2026-09-24) — the
          880px cap is gone: the wizard now fills the standard `.page` width
          (1200px, centred) so the two-column grid uses a wide window instead of
          leaving the right half empty. A deliberate departure from the V1 width. */}
      <h2 className="screen-title" style={{ margin: '0 0 6px 0' }}>
        {supplementParentId ? 'New Supplement' : 'New Submission'}
      </h2>
      <div className="screen-subtitle" style={{ marginBottom: 20 }}>
        Step {step + 1} of {STEP_LABELS.length} — {STEP_LABELS[step]}
      </div>

      {/* phase-fix-new-supplement-routing — FAIL LOUD: an unresolvable
          ?supplementOf id never degrades to the generic wizard. */}
      {supplementError && (
        <div
          role="alert"
          data-testid="supplement-context-error"
          style={{
            padding: '10px 12px',
            marginBottom: 16,
            border: '1px solid var(--red, #dc2626)',
            background: 'rgba(220,38,38,0.08)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--red, #b91c1c)',
          }}
        >
          {supplementError}
        </div>
      )}
      {supplementParentId && !supplementError && (
        <div
          data-testid="supplement-context-note"
          style={{
            padding: '10px 12px',
            marginBottom: 16,
            border: '1px solid var(--accent, #2563eb)',
            background: 'var(--accent-soft, rgba(59,130,246,0.08))',
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          Continuing <strong>{form.drugName}</strong> ({form.region}) — the application identity is
          inherited and locked. Choose the supplement's details below.
        </div>
      )}

      <ol
        aria-label="Wizard progress"
        data-testid="wizard-progress"
        style={{
          display: 'flex',
          gap: 8,
          listStyle: 'none',
          padding: 0,
          margin: '0 0 24px 0',
        }}
      >
        {STEP_LABELS.map((label, idx) => {
          const isActive = idx === step;
          const isDone = idx < step;
          return (
            <li
              key={label}
              aria-current={isActive ? 'step' : undefined}
              data-testid={`wizard-step-${idx}`}
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                background: isActive
                  ? 'var(--accent)'
                  : isDone
                    ? 'var(--green-bg)'
                    : 'var(--surface2)',
                color: isActive ? '#fff' : isDone ? 'var(--green)' : 'var(--text-muted)',
                border: '1px solid var(--border)',
                textAlign: 'center',
              }}
            >
              {idx + 1}. {label}
            </li>
          );
        })}
      </ol>

      <div style={{ marginBottom: 24 }}>
        {step === 0 && (
          <Step1Application
            form={form}
            errors={visibleErrors}
            update={update}
            companies={companies}
            companyError={companyError}
            lockedIdentity={supplementParentId !== null}
          />
        )}
        {step === 1 && (
          <>
            <Step2Submission
              form={form}
              errors={visibleErrors}
              update={update}
              specPreview={specPreview}
              catalogByAppType={catalogByAppType}
              catalogAppTypes={catalogAppTypes}
              catalogLoading={catalogLoading}
              onAppTypeChange={handleAppTypeChange}
              onFilingTypeChange={handleFilingTypeChange}
              onIdentifierBlur={handleIdentifierBlur}
              lookupStatus={lookupStatus}
              lookupMessage={lookupMessage}
              sequenceDuplicate={dupCheck.status === 'found' ? dupCheck : null}
              uniquenessNotice={
                dupCheck.status === 'loading'
                  ? 'Checking that this sequence is not already taken…'
                  : dupCheck.status === 'error'
                    ? dupCheck.message
                    : null
              }
              onUseNextSequence={applyNextSequence}
              lockedIdentity={supplementParentId !== null}
            />
            {/* Phase 7.6 — Parallel multi-region toggle */}
            <div
              data-testid="parallel-mode-section"
              style={{
                marginTop: 20,
                padding: '14px 16px',
                border: '1px solid var(--border)',
                borderRadius: 8,
                background: 'var(--surface2)',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  data-testid="parallel-mode-toggle"
                  checked={parallelMode}
                  onChange={(e) => {
                    setParallelMode(e.target.checked);
                    if (!e.target.checked) setAdditionalRegions([]);
                  }}
                />
                <span style={{ fontWeight: 600, fontSize: 13 }}>
                  Create parallel multi-region submission
                </span>
              </label>
              {parallelMode && (
                <div
                  data-testid="parallel-regions-selector"
                  style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
                    Select additional regions. Each is a separate application — give each its own
                    application type, submission type and market identifier below. Only the product
                    and company details are shared; identifiers are never copied between markets.
                  </p>
                  {REGIONS.filter((r) => r !== form.region).map((r) => (
                    <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        data-testid={`parallel-region-${r}`}
                        checked={additionalRegions.includes(r)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setAdditionalRegions((prev) =>
                            checked ? [...prev, r] : prev.filter((x) => x !== r),
                          );
                          setRegionSetups((prev) => {
                            const next = { ...prev };
                            if (checked) {
                              if (!next[r]) next[r] = emptyRegionDraft();
                            } else {
                              delete next[r];
                            }
                            return next;
                          });
                        }}
                      />
                      {REGION_FLAGS[r as keyof typeof REGION_FLAGS]} {r}
                    </label>
                  ))}
                  {additionalRegions.length > 0 && (
                    <>
                      <ParallelRegionSetups
                        regions={additionalRegions}
                        value={regionSetups}
                        onChange={(region, draft) =>
                          setRegionSetups((prev) => ({ ...prev, [region]: draft }))
                        }
                        showErrors={showErrors}
                        flags={REGION_FLAGS}
                      />
                      <p
                        data-testid="parallel-summary"
                        style={{ margin: 0, fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}
                      >
                        Will create {additionalRegions.length + 1} linked submissions — 1 primary (
                        {form.region}) + {additionalRegions.length} additional.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
        {step === 2 && (
          <Step3Sequence form={form} errors={visibleErrors} update={update} />
        )}
        {step === 3 && (
          <>
            <Step4Review form={form} companies={companies} specPreview={specPreview} catalogByAppType={catalogByAppType} />
            {parallelMode && additionalRegions.length > 0 && (
              <section aria-label="Additional regional applications" data-testid="parallel-review" style={{ marginTop: 20 }}>
                <h3>Additional regional applications</h3>
                {additionalRegions.map(region => {
                  const draft = regionSetups[region] ?? emptyRegionDraft();
                  return <dl key={region} style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                    <dt style={{ fontWeight: 600 }}>{region}</dt>
                    <dd>Application type: {draft.applicationType || 'Missing'}</dd>
                    <dd>Submission type: {draft.submissionTypeCode || 'Missing'}{draft.submissionSubTypeCode ? ` / ${draft.submissionSubTypeCode}` : ''}</dd>
                    <dd>{identifierSpecForRegion(region)?.label ?? 'Application identifier'}: {draft.identifier || 'Missing'}</dd>
                    <dd>Initial sequence: {initialSequenceNumberForRegion(region)}</dd>
                  </dl>;
                })}
              </section>
            )}
          </>
        )}
      </div>

      {submitError && (
        <div role="alert" style={{ color: 'var(--red)', marginBottom: 16, fontSize: 13 }}>
          {submitError}
        </div>
      )}

      {/* phase-ui-review-F10 — the global AI launcher is fixed at the viewport's
          bottom-right (AppShell: right 20 / bottom 20, 48px). At narrow widths
          it overlapped the primary wizard action (Next / Create submission).
          Reserve a clear zone on the right of the action cluster and below the
          footer so the launcher never lands on a button. The launcher's own
          size/position persistence is untouched — this only yields space. */}
      <div
        data-testid="wizard-footer"
        style={{
          display: 'flex',
          gap: 12,
          justifyContent: 'space-between',
          paddingBottom: FAB_SAFE_AREA_PX,
        }}
      >
        <div>
          <button
            type="button"
            onClick={() => navigate('/home')}
            disabled={submitting}
            style={secondaryButton}
          >
            Cancel
          </button>
        </div>
        <div
          data-testid="wizard-footer-actions"
          style={{ display: 'flex', gap: 12, paddingRight: FAB_SAFE_AREA_PX }}
        >
          <button
            type="button"
            onClick={goBack}
            disabled={step === 0 || submitting}
            style={{
              ...secondaryButton,
              opacity: step === 0 || submitting ? 0.5 : 1,
              cursor: step === 0 || submitting ? 'not-allowed' : 'pointer',
            }}
          >
            Back
          </button>
          {!isLastStep && (
            <button type="button" onClick={() => void goNext()} style={primaryButton}>
              Next
            </button>
          )}
          {isLastStep && (
            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitting}
              style={{
                ...primaryButton,
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Creating…' : 'Create submission'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
